import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Box,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useController } from "react-hook-form";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import "../../styles/fontFaces.css";
import useMediaQuery from "@mui/material/useMediaQuery";

const SurveyQuestion = ({
  errors,
  question,
  options,
  required,
  name,
  control,
  spacingTop,
  spacingBottom,
  maxCharacters = 50,
  namespace,
}) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const requiresCustomInput = options.some(
    (option) => option.value === value && option.customInput,
  );

  const { t } = useTranslation(namespace);
  const theme = useTheme();
  const {
    field: {
      ref: customInputRef,
      value: customInputValue,
      onChange: customInputChange,
      onBlur: customInputBlur,
    },
    fieldState: { error: customInputError },
  } = useController({
    name: `${name}_unlisted`,
    control,
    defaultValue: "",
  });

  const hasIncompleteRequiredQuestionError =
    (question === t("suicideQuestion.title") && errors.thoughtsOfSuicide) ||
    (question === t("upsetQuestion.title") && errors.upsetAmount);

  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <FormControl
      fullWidth
      error={Boolean(error || (requiresCustomInput && customInputError))}
      sx={{
        marginTop: spacingTop,
        marginBottom: spacingBottom,
      }}
    >
      <FormLabel
        sx={{
          ...theme.typography.h2,
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
          "&.Mui-focused": {
            color: theme.palette.secondary.main,
          },
          "&.Mui-error": {
            color: theme.palette.darkRed,
          },
          color: hasIncompleteRequiredQuestionError
            ? theme.palette.darkRed
            : theme.palette.secondary.main,
          mb: 1,
          paddingBottom: isDesktop || required ? "5px" : "15px",
        }}
      >
        {question}
        {required ? (
          <span
            style={{
              color: theme.palette.darkRed,
              fontFamily: "Public Sans",
              fontStyle: "italic",
              fontWeight: 400,
              fontSize: "13px",
            }}
          >
            {" "}
            {t("questionText.required", { ns: "common" })}
          </span>
        ) : null}
      </FormLabel>
      {hasIncompleteRequiredQuestionError && (
        <Box
          sx={{
            borderTop: `1px solid ${theme.palette.darkRed}`,
            borderBottom: `1px solid ${theme.palette.darkRed}`,
            height: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            backgroundColor: theme.palette.translucentWarmRed,
            mb: 1,
          }}
        >
          <Typography
            sx={{
              ml: "50px",
              color: theme.palette.darkRed,
              fontFamily: "Public Sans",
              fontWeight: 600,
              fontSize: "15px",
            }}
          >
            {t("questionText.requiredField", { ns: "common" })}
          </Typography>
        </Box>
      )}
      <RadioGroup onChange={onChange} value={value} onBlur={onBlur}>
        {options.map((option) => (
          <Box
            key={option.value}
            tabIndex="0"
            onKeyDown={(e) => {
              const targetTag = e.target.tagName.toLowerCase();
              if (
                (e.key === "Enter" || e.key === " ") &&
                targetTag !== "input" &&
                targetTag !== "textarea"
              ) {
                e.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              borderRadius: 2,
              mb: isDesktop ? "4px" : "8px",
              py: isDesktop ? "0px" : "5px",
              minHeight: isDesktop ? "44px" : "45px",
              bgcolor:
                value === option.value
                  ? theme.palette.primary.light
                  : theme.palette.white,
              pl: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              width: "100%",
              boxSizing: "border-box",
              border: isDesktop
                ? "1px solid transparent"
                : `.25px solid ${theme.palette.secondary.main}`,
              outline: "none",
              "&:hover": {
                color: theme.palette.primary.main,
                bgcolor:
                  value === option.value
                    ? theme.palette.primary.light
                    : theme.palette.secondary.light,
                border:
                  value !== option.value
                    ? `1px solid ${theme.palette.primary.main}`
                    : "1px solid transparent",
              },
              "&:focus-visible": {
                outline: `1px dashed ${theme.palette.secondary.main}`,
                outlineOffset: isDesktop ? "1px" : "2px",
                borderRadius: "8px",
              },
              transition: "none",
            }}
          >
            <FormControlLabel
              value={option.value}
              control={
                <Radio
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    py: isDesktop ? "6px" : "9px",
                    minHeight: isDesktop ? "44px" : "45px",
                    color: theme.palette.secondary.dark,
                    "&.Mui-checked": {
                      color: theme.palette.white,
                    },
                  }}
                />
              }
              label={option.label}
              sx={{
                "& .MuiTypography-root": {
                  color:
                    value === option.value
                      ? theme.palette.white
                      : theme.palette.secondary.dark,
                  fontWeight: value === option.value ? "700" : "400",
                  fontSize: 16,
                },
                width: "100%",
                mb: 0,
              }}
            />
            {value === option.value && option.customInput && (
              <Box sx={{ width: "100%", position: "relative", mt: 0, pb: 5 }}>
                <TextField
                  ref={customInputRef}
                  value={customInputValue}
                  onChange={(e) => {
                    customInputChange(e.target.value);
                  }}
                  onBlur={customInputBlur}
                  placeholder={t("questionText.clarify", { ns: "common" })}
                  fullWidth
                  focused
                  InputProps={{
                    sx: { fontSize: 16 },
                    inputProps: { maxLength: maxCharacters },
                  }}
                  InputLabelProps={{ sx: { fontSize: 16 } }}
                  sx={{
                    bgcolor: theme.palette.white,
                    borderRadius: "8px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      "&.Mui-focused fieldset": {
                        borderColor: "transparent",
                      },
                    },
                    width: "calc(100% - 16px)",
                  }}
                />
                <Typography
                  sx={{
                    position: "absolute",
                    top: "65px",
                    left: "10px",
                    color: theme.palette.white,
                    fontSize: "14px",
                  }}
                >
                  {customInputValue.length === 0
                    ? `${maxCharacters} ${t("questionText.maximumCharacters", { ns: "common" })}`
                    : customInputValue.length === maxCharacters - 1
                      ? `${maxCharacters - customInputValue.length} ${t("questionText.characterRemaining", { ns: "common" })}`
                      : `${maxCharacters - customInputValue.length} ${t("questionText.charactersRemaining", { ns: "common" })}`}
                </Typography>
              </Box>
            )}
          </Box>
        ))}
      </RadioGroup>
      {requiresCustomInput && customInputError && (
        <FormHelperText>{customInputError.message}</FormHelperText>
      )}
    </FormControl>
  );
};

SurveyQuestion.propTypes = {
  errors: PropTypes.object,
  question: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      customInput: PropTypes.bool,
    }),
  ).isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  required: PropTypes.bool,
  spacingTop: PropTypes.number,
  spacingBottom: PropTypes.number,
  maxCharacters: PropTypes.number,
  namespace: PropTypes.string.isRequired,
};

export default SurveyQuestion;
